import * as React from 'react';
import { FormEvent, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux';
import { TDispatch } from '../../../types/Thunk';
import { useApp } from '../../../hooks/useApp';
import { IAppState } from '../../../store/Store';
import { useInspection } from '../../../hooks/useInspection';
import { useTranslation } from 'react-i18next';
import {
  deleteInspectionPhoto,
  reportInspectionPhotos,
  setInspectionPhotoSent,
  setInspectionQuestion,
  setInspectionQuestionId,
  TInspectionPhotoRequest,
  uploadPhoto,
} from '../../../actions/InspectionActions';
import {
  DeleteInspectionPhoto, ReportInspectionPhotos,
  TApiInspectionPhoto,
  TInspectionState,
  UploadInspectionPhoto,
} from '../../../types/Inspection';
import AlertBlock from '../../../components/AlertBlock';
import Typography from '@mui/material/Typography';
import InspectionPhotoSelect from '../../../components/inspection/InspectionPhotoSelect';
import InspectionPhotoList from '../../../components/inspection/InspectionPhotoList';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { loadStoredRespondentState } from '../../../hooks/useCurrentActiveSurvey';
import { useContentState } from '../../../hooks/useСontentState';
import { setCurrentPageTitle } from '../../../actions/ContentActions';
import SurveyTranslation from '../../../components/survey/SurveyTranslation';
import FormHelperText from '@mui/material/FormHelperText';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      width: '100%',
      marginTop: theme.typography.pxToRem(16),
      marginBottom: theme.typography.pxToRem(16),
    },
    submit: {
      margin: theme.spacing(3, 0, 3),
    },
    alertContainer: {
      width: '100%',
    },
    personalContainer: {
      border: '2px solid #6FADE7',
      padding: theme.spacing(2),
      width: '100%',
    },
    fileCopy: {
      padding: theme.spacing(0),
      margin: theme.spacing(0),
      textAlign: 'right',
    },
    title: {
      marginTop: '1em',
    },
  })
);
const InspectionImagePage: React.FC<RouteComponentProps<any>> = ({
  history,
}): JSX.Element => {
  const dispatch = useDispatch<TDispatch>();
  const { app } = useApp();
  const { isCasePagePublished } = useContentState();
  const inspectionState: TInspectionState = useSelector<IAppState, TInspectionState>(
    (state) => state.inspectionState
  );
  const { inspectionQustionId } = useInspection();
  const classes = useStyles({});
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [errorOccurred, setErrorOccurred] = useState<any>(false);
  const [error, setError] = useState<any>(null);

  const respondentIdentifier = loadStoredRespondentState();
  if (!respondentIdentifier) {
    throw new Error(`No stored respondent`);
  }

  useEffect(
    () => () => {
      dispatch(setInspectionQuestion(false));
      dispatch(setInspectionQuestionId(0));
    },
    //eslint-disable-next-line
    []
  );

  useEffect(() => {
    dispatch(setCurrentPageTitle(
      t("inspection.media.title", {
        lng: respondentIdentifier.lang,
      })
    ));
  }, [dispatch, t, respondentIdentifier.lang]);

  const deleteUploadedPhoto = async (photoId: number) => {
    await dispatch(deleteInspectionPhoto(photoId)).catch(() => {
      setErrorOccurred(true);
    });
  };

  const handlePhotoUpload = async(fileName: string, base64Content: string) => {
    await dispatch(uploadPhoto(fileName, base64Content)).catch(() => {
      setErrorOccurred(true)
    });
  };

  const submitInspectionMedia = async (e: FormEvent) => {
    e.preventDefault();

    const photos: Array<number> = [];
    if (inspectionState.photos && inspectionState.photos.length > 0) {
      inspectionState.photos.forEach((photo: TApiInspectionPhoto) => {
        return photos.push(photo.id);
      })
    }

    const requestData: TInspectionPhotoRequest = {
      photos,
      comment,
      respondentId: respondentIdentifier.id,
      location: inspectionState.locationId,
      questionId: inspectionQustionId,
    };

    await dispatch(reportInspectionPhotos(requestData))
      .then(() => {
        setError(null);
        dispatch(setInspectionPhotoSent(true));
        dispatch(setInspectionQuestionId(0));
        history.goBack();
      })
      .catch(() => {
        setErrorOccurred(true);
      })
      .finally(() => {
        window.scrollTo(0, 0);
      });
  };

  useEffect(() => {
    if (app.hasError(UploadInspectionPhoto)) {
      setError(app.getError(UploadInspectionPhoto))
    }
    if (app.hasError(DeleteInspectionPhoto)) {
      setError(app.getError(DeleteInspectionPhoto))
    }
    if (app.hasError(ReportInspectionPhotos)) {
      setError(app.getError(ReportInspectionPhotos))
    }
  }, [app]);

  return (
    <>
      {error && errorOccurred && (
        <AlertBlock type={'error'}>{error?.message}</AlertBlock>
      )}

      <form onSubmit={submitInspectionMedia}>
        <Typography>
          {
            isCasePagePublished ?
              <SurveyTranslation translate={"inspection.media.header_with_case"} />
              :
              <SurveyTranslation translate={"inspection.media.header"} />
          }
        </Typography>

        {error && errorOccurred && (
          <AlertBlock type={'error'}>{error?.fields?.photos}</AlertBlock>
        )}
        <InspectionPhotoSelect handlePhotoUpload={handlePhotoUpload} accept='*'/>
        <FormHelperText variant="filled">
          <SurveyTranslation translate={"inspection.media.media_helptext"} />
        </FormHelperText>
        {inspectionState.photos && inspectionState.photos.length > 0 && (
          <InspectionPhotoList
            photos={inspectionState.photos}
            deleteUploadedPhoto={deleteUploadedPhoto}
          />
        )}

        <TextField
          variant='outlined'
          margin='normal'
          fullWidth
          name='comment'
          label={<SurveyTranslation translate={"inspection.media.comment"} />}
          type='text'
          id='problem'
          multiline
          rows={3}
          value={comment}
          helperText={<SurveyTranslation translate={"inspection.media.comment_helptext"} />}
          onChange={(e) => setComment(e.target.value)}
        />

        <Grid container spacing={1} justifyContent="space-around">
          <Grid item xs={6}>
            <Button
              type='submit'
              fullWidth
              variant='contained'
              color='primary'
              className={classes.submit}
            >
              <SurveyTranslation translate={"inspection.media.submit.form.button"} />
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  )
}

export default withRouter(InspectionImagePage);